export interface ReelTimingOptions {
  minDwellSec?: number;
  maxDwellSec?: number;
  crossfadeMs?: number;
}

export interface ReelPlan {
  photoIndices: number[];
  dwellMsPerSlot: number[];
  crossfadeMs: number;
}

const DEFAULT_MIN_DWELL_SEC = 2.5;
const DEFAULT_MAX_DWELL_SEC = 6;
const DEFAULT_CROSSFADE_MS = 700;

// Pure - no DOM, no appState. Given how many photos an album has and how
// long the chosen track runs, decides which photo plays in which slot and
// for how long. Three regimes:
// - Too many photos to give each one at least minDwellSec: subsample down
//   to as many evenly-spaced photos as fit at minDwellSec each.
// - Too few photos to reach maxDwellSec per photo without running past the
//   track: loop the photo set (repeating from the start) until enough
//   slots exist to fill the duration at maxDwellSec each.
// - Otherwise: one slot per photo, dwell = duration / photoCount.
export function computeReelPlan(photoCount: number, durationSec: number, options: ReelTimingOptions = {}): ReelPlan {
  const minDwellSec = options.minDwellSec ?? DEFAULT_MIN_DWELL_SEC;
  const maxDwellSec = options.maxDwellSec ?? DEFAULT_MAX_DWELL_SEC;
  const crossfadeMs = options.crossfadeMs ?? DEFAULT_CROSSFADE_MS;

  if (photoCount <= 0 || durationSec <= 0) {
    return { photoIndices: [], dwellMsPerSlot: [], crossfadeMs };
  }

  const idealDwellSec = durationSec / photoCount;

  if (idealDwellSec < minDwellSec) {
    const slotCount = Math.max(1, Math.min(photoCount, Math.floor(durationSec / minDwellSec)));
    const dwellSec = durationSec / slotCount;
    const photoIndices = Array.from({ length: slotCount }, (_, i) => Math.floor((i * photoCount) / slotCount));
    return { photoIndices, dwellMsPerSlot: photoIndices.map(() => dwellSec * 1000), crossfadeMs };
  }

  if (idealDwellSec > maxDwellSec) {
    const slotCount = Math.max(1, Math.ceil(durationSec / maxDwellSec));
    const dwellSec = durationSec / slotCount;
    const photoIndices = Array.from({ length: slotCount }, (_, i) => i % photoCount);
    return { photoIndices, dwellMsPerSlot: photoIndices.map(() => dwellSec * 1000), crossfadeMs };
  }

  const photoIndices = Array.from({ length: photoCount }, (_, i) => i);
  return { photoIndices, dwellMsPerSlot: photoIndices.map(() => idealDwellSec * 1000), crossfadeMs };
}
